import React, { useState, useEffect } from 'react';
import { Play, Square, Check, X } from 'lucide-react';
import { audioVolumeManager } from '../../services/audioVolumeManager';
import './VoiceSelector.css';

const VoiceSelector = ({ isOpen, onClose, currentVoice, onVoiceChange }) => {
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(currentVoice);
  const [playingVoice, setPlayingVoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audioElement, setAudioElement] = useState(null);

  // Sample audio paths - use PUBLIC_URL for built app compatibility
  const sampleAudios = {
    lessac: `${process.env.PUBLIC_URL}/audio/samples/lessac-sample.wav`,
    amy: `${process.env.PUBLIC_URL}/audio/samples/amy-sample.wav`,
    hfc_female: `${process.env.PUBLIC_URL}/audio/samples/hfc_female-sample.wav`,
    hfc_male: `${process.env.PUBLIC_URL}/audio/samples/hfc_male-sample.wav`,
    alba: `${process.env.PUBLIC_URL}/audio/samples/alba-sample.wav`
  };

  useEffect(() => {
    if (isOpen) {
      fetchVoices();
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedVoice(currentVoice);
  }, [currentVoice]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
    };
  }, [audioElement]);

  const fetchVoices = async () => {
    setLoading(true);
    // MOCK DATA for static demo
    // Matching backend definitions from app/api/voice_settings.py
    const mockVoices = [
      { id: 'lessac', name: 'Lessac', gender: 'Female', accent: 'US' },
      { id: 'amy', name: 'Amy', gender: 'Female', accent: 'US' },
      { id: 'hfc_female', name: 'Sarah', gender: 'Female', accent: 'US' },
      { id: 'hfc_male', name: 'David', gender: 'Male', accent: 'US' },
      { id: 'alba', name: 'Alba', gender: 'Female', accent: 'UK' }
    ];

    setTimeout(() => {
      setVoices(mockVoices);
      setLoading(false);
    }, 500); // Simulate network delay
  };

  const playVoiceSample = (voiceId) => {
    // If clicking the same voice that's playing, stop it
    if (playingVoice === voiceId) {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
      setPlayingVoice(null);
      return;
    }

    // Stop any currently playing audio
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
      audioElement.onended = null;
      audioElement.onerror = null;
    }

    // Create new audio element
    const audio = new Audio(sampleAudios[voiceId]);

    // Apply app volume
    audio.volume = audioVolumeManager.getVolume() / 100;

    // Set up event listeners before playing
    audio.onended = () => {
      setPlayingVoice(null);
      setAudioElement(null);
    };

    audio.onerror = () => {
      console.error('Error loading audio sample for', voiceId);
      setPlayingVoice(null);
      setAudioElement(null);
    };

    // Update state and play
    setAudioElement(audio);
    setPlayingVoice(voiceId);

    audio.play().catch(err => {
      console.error('Error playing audio:', err);
      setPlayingVoice(null);
      setAudioElement(null);
    });
  };

  const handleVoiceSelect = async (voiceId) => {
    setSelectedVoice(voiceId);

    // Mock backend update for demo
    console.log('✅ (Demo) Voice preference updated locally:', voiceId);
    localStorage.setItem('sarvik_voice_preference', voiceId);
    onVoiceChange(voiceId);
  };

  const handleClose = () => {
    // Stop any playing audio
    if (audioElement) {
      audioElement.pause();
      setPlayingVoice(null);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="voice-selector-overlay" onClick={handleClose}>
      <div className="voice-selector-modal" onClick={(e) => e.stopPropagation()}>
        <div className="voice-selector-header">
          <h2>Select Voice</h2>
          <button className="close-button" onClick={handleClose}>
            <X size={24} />
          </button>
        </div>

        {loading ? (
          <div className="voice-selector-loading">
            <div className="spinner"></div>
            <p>Loading voices...</p>
          </div>
        ) : (
          <div className="voice-grid">
            {voices.map((voice) => (
              <div
                key={voice.id}
                className={`voice-option ${selectedVoice === voice.id ? 'selected' : ''}`}
                onClick={() => handleVoiceSelect(voice.id)}
              >
                <button
                  className="play-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    playVoiceSample(voice.id);
                  }}
                  aria-label={`Play ${voice.name} sample`}
                >
                  {playingVoice === voice.id ? (
                    <Square size={20} fill="currentColor" />
                  ) : (
                    <Play size={20} fill="currentColor" />
                  )}
                </button>

                <div className="voice-info">
                  <div className="voice-name-row">
                    <span className="voice-name">{voice.name}</span>
                    <span className={`voice-badge ${voice.gender.toLowerCase()}`}>
                      {voice.gender}
                    </span>
                  </div>
                  {voice.accent && (
                    <span className="voice-accent">{voice.accent}</span>
                  )}
                </div>

                {selectedVoice === voice.id && (
                  <div className="check-icon">
                    <Check size={20} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="voice-selector-footer">
          <p className="hint-text">
            Click the play button to preview each voice
          </p>
        </div>
      </div>
    </div>
  );
};

export default VoiceSelector;
